// mui
import { Grid, Paper, Typography } from '@mui/material';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { useContext } from 'react';

// types
import { TransactionGroupItem } from '../../../../../../@types/index';

// ui
import { Table, TableHeader } from '../../../../../ui/Table/Table';
import { formatCurrency } from '../../../../../../utils/currency';

// context
import { ContextSoeAccounts, ContextSoeFilters, ContextSoeTransactions } from '../Soe.context';

// theme
import { theme } from '../../../../../../theme';

// utils
import { groupTransactions, getGroupDateHeaders } from '../../../../../../utils/soe';

export const Summary = () => {
	// hooks
	const { t } = useTranslation();

	// context
	const { accounts } = useContext(ContextSoeAccounts);
	const { filtersValues } = useContext(ContextSoeFilters);
	const { columns, isLoading, transactionsGrouping } = useContext(ContextSoeTransactions);

	// determine group keys based on checked columns
	const groupKeys = columns.filter((c) => c.checked).map((col) => col.key);
	const { monthHeaders, yearMonthMap } = getGroupDateHeaders({
		transactionsGrouping,
	});

	const fromAccount = accounts.find((acc) => acc.id === Number(filtersValues.fromAccount));

	// generate grouped data
	const data = transactionsGrouping?.length
		? groupTransactions({
				currency: fromAccount?.currency,
				groupKeys,
				monthHeaders,
				t,
				transactionsGrouping,
		  })
		: [];

	const headers = [
		{
			label: t('group'),
			render: (value: string, header: TableHeader, row: TransactionGroupItem) => {
				const isBold = row.indent === 0 || row.indent === 1;
				return (
					<>
						<Typography
							fontWeight={600}
							paddingLeft={`${row.indent * 20}px`}
							style={{ fontWeight: isBold ? 600 : 400 }}>
							{value}
						</Typography>
						{row.type && (
							<Typography
								color="brand.grey.500"
								paddingLeft={`${row.indent * 20}px`}
								variant="caption">
								{t(row.type)}
							</Typography>
						)}
					</>
				);
			},
			value: 'name',
		},
		...monthHeaders.map((monthYear) => ({
			label: yearMonthMap.get(monthYear) || monthYear,
			render: (value: number, header: TableHeader, row: TransactionGroupItem) => {
				const isBold = row.indent === 0 || row.indent === 1;
				return (
					<Typography style={{ fontWeight: isBold ? 600 : 400 }}>
						{formatCurrency({ currency: row.currency, value: value || 0 })}
					</Typography>
				);
			},
			value: monthYear,
		})),
		{
			label: t('total'),
			render: (total: number, header: TableHeader, row: TransactionGroupItem) => {
				const isBold = row.indent <= 1 || (row.isTotalRow && row.indent <= 1);
				return (
					<Typography style={{ fontWeight: isBold ? 600 : 400 }}>
						{formatCurrency({ currency: row.currency, value: total || 0 })}
					</Typography>
				);
			},
			value: 'total',
		},
		{
			label: t('totalPaidFromDa'),
			render: (totalPaidFromDa: number, header: TableHeader, row: TransactionGroupItem) => {
				const isBold = row.indent <= 1 || (row.isTotalRow && row.indent <= 1);
				return (
					<Typography style={{ fontWeight: isBold ? 600 : 400 }}>
						{formatCurrency({ currency: row.currency, value: totalPaidFromDa || 0 })}
					</Typography>
				);
			},
			value: 'totalPaidFromDa',
		},
	];

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Paper>
					<Table
						borderRadius={2}
						btnExpandPlacement="start"
						color="blue"
						data={data}
						EmptyProps={{
							hasContent: true,
							hasTitle: true,
						}}
						hasDividers={true}
						headers={headers}
						isLoading={isLoading}
						sxTable={{
							'& tbody tr:last-child': {
								backgroundColor: data.length ? theme.palette.brand.grey[100] : 'transparent',
							},
						}}
						sxTd={{
							paddingTop: theme.spacing(1),
							paddingBottom: theme.spacing(1),
						}}
					/>
				</Paper>
			</Grid>
		</Grid>
	);
};
